/* eslint-disable prettier/prettier */
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Formik, FormikValues } from 'formik';
import { RootState } from 'store';
import { ThemeProvider } from 'styled-components';
import * as yup from 'yup';

import Header from 'components/AppBar';
import SubmitButton from 'components/Buttons/SubmitButton';
import { customTheme } from 'components/DesktopFlightsSearch/customTheme';
import Input from 'components/Input';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import { RenderProps as SelectRenderProps } from 'components/Selects/BookingSelectDate';
import CitiesAutoCompleteSelect from 'components/Selects/CitiesAutoCompleteSelect';
import CountriesAutoCompleteSelect from 'components/Selects/CountriesAutoCompleteSelect';
import ProvincesAutoCompleteSelect, {
  RenderProps as ProvinceSelectRenderProps,
} from 'components/Selects/ProvincesAutoCompleteSelect';
import AutoCompleteCitiesSearchContainer from 'containers/AutoCompleteCities';
import AutoCompleteCountriesSearchContainer from 'containers/AutoCompleteCountries';
import AutoCompleteProvincesSearchContainer from 'containers/AutoCompleteProvinces';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { CityAutoComplete } from 'models/cityAutoComplete';
import { CompanyAddressRequest } from 'models/company';
import { CountryAutoComplete } from 'models/countryAutoComplete';
import { ProvinceAutoComplete } from 'models/provinceAutoComplete';
import { saveCompanyAddress } from 'services/companyService';
import { getCityByCode, getProvinceByCode } from 'services/locationService';
import { extractErrorMessage } from 'utils/generic';

import { Container, Content, Wrapper, FormContainer, Title, Field, LocationFields, ButtonContainer } from './styles';

interface ICompanyBillingMapForm {
  address: string;
  description: string;
  zipCode: string;
}

const CompanyBillingMapForm = () => {
  const history = useHistory();
  const { t } = useStoreTranslation();
  const toast = useToast();
  const { company } = useSelector((state: RootState) => state.companyBilling);

  const [loading, setLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [countryCode, setCountryCode] = useState<CountryAutoComplete | undefined>();
  const [provinceCode, setProvinceCode] = useState<ProvinceAutoComplete | undefined>();
  const [cityCode, setCityCode] = useState<CityAutoComplete | undefined>();

  // Precarga País con el valor ya guardado (company.address.country es el código ISO
  // del país, igual que company.countryId). No depende de ningún autocomplete: el nombre
  // se resuelve con las traducciones que ya existen en countries.*.
  useEffect(() => {
    const savedCountryCode = company?.address?.country_code || company?.address?.country;
    if (savedCountryCode && !countryCode) {
      setCountryCode({
        countryCode: savedCountryCode,
        countryName: t(`countries.${savedCountryCode}`),
        cityCode: '',
        cityName: '',
        stateName: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.address?.country]);

  // Precarga Ciudad: solo se guarda el city_code (no el nombre). Travels/data/AutoComplete/Cities
  // filtra por texto contenido en el nombre, así que pasarle el código como texto no matchea nada
  // — se usa Travels/GetByCityCode/{code}, que sí busca por código exacto.
  useEffect(() => {
    const savedCityCode = company?.address?.city_code;
    const savedCountryCodeForCity = company?.address?.country_code || company?.address?.country || '';
    if (savedCityCode && !cityCode) {
      getCityByCode(savedCityCode)
        .then((result) => {
          if (result?.cityName) {
            setCityCode({
              cityCode: savedCityCode,
              cityName: result.cityName,
              countryCode: savedCountryCodeForCity,
              countryName: result.countryName,
              stateName: result.stateName,
            });
          }
        })
        .catch(() => {
          // si falla, el campo simplemente queda vacío para que el usuario lo seleccione a mano
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.address?.city_code]);

  // Precarga Provincia: mismo motivo que Ciudad. Travels/GetProvinces filtra por texto en el
  // nombre, así que se usa Travels/GetProvinceByCode, que busca por ProvinceCode exacto.
  useEffect(() => {
    const savedProvinceCode = company?.address?.province_code;
    if (savedProvinceCode && !provinceCode) {
      getProvinceByCode(savedProvinceCode)
        .then((results) => {
          const match = results?.find((p) => p.provinceCode === savedProvinceCode) || results?.[0];
          if (match) setProvinceCode(match);
        })
        .catch(() => {
          // si falla, el campo simplemente queda vacío para que el usuario lo seleccione a mano
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.address?.province_code]);

  const handleSubmit = useCallback(
    async (values: ICompanyBillingMapForm) => {
      if (!countryCode || !cityCode) {
        toast.add({
          type: 'danger',
          message: t('billing.map.error.countryCity'),
        });
        return;
      }

      if (countryCode.countryCode !== company?.countryId) {
        toast.add({
          type: 'danger',
          message: t('billing.map.error.differentCountry', { country: t(`countries.${company?.countryId}`) }),
        });
        return;
      }

      setIsSubmitting(true);
      setLoading(true);

      // NOTA: lat/lng ya no se obtienen de un geocode (se eliminó el mapa). Se envían en 0
      // como placeholder hasta confirmar con backend si el endpoint los necesita realmente.
      const addressForm: CompanyAddressRequest = {
        address: values.address,
        description: values.description,
        lat: 0,
        lng: 0,
        city_code: cityCode.cityCode,
        province_code: provinceCode?.provinceCode || '',
        country_code: countryCode.countryCode,
        zip_code: values.zipCode,
      };

      try {
        await saveCompanyAddress(addressForm);
        history.push('/corporate/companyBilling', history.location.state);
      } catch (e) {
        toast.add({
          type: 'danger',
          message: e.error || e.message || extractErrorMessage(e),
        });
      } finally {
        setLoading(false);
        setIsSubmitting(false);
      }
    },
    [cityCode, company?.countryId, countryCode, history, provinceCode, t, toast],
  );

  const validationSchema = yup.object().shape({
    address: yup.string().trim().required(),
    description: yup.string().trim().max(100),
    zipCode: yup.string().trim().max(20),
  });

  const initValues: ICompanyBillingMapForm = {
    address: company?.address?.address || '',
    description: company?.address?.description || '',
    zipCode: company?.address?.zip_code || '',
  };

  const handleBack = () => {
    history.push('/corporate/companyBilling', history.location.state);
  };

  const getCountrySelectValue = (country?: CountryAutoComplete) => {
    if (!country) return undefined;
    return { name: country.countryName, detail: country.countryCode };
  };

  const getProvinceSelectValue = (province?: ProvinceAutoComplete) => {
    if (!province) return undefined;
    return { name: province.name, detail: province.provinceCode };
  };

  const getCitySelectValue = (city?: CityAutoComplete) => {
    if (!city) return undefined;
    return { name: city.cityName, detail: `${city.cityCode}, ${city.cityName}` };
  };

  const renderCountrySelect = () => (
    <ThemeProvider theme={customTheme}>
      <CountriesAutoCompleteSelect
        label={t('billing.map.country')}
        detail={t('billing.map.countryPlaceholder')}
        value={getCountrySelectValue(countryCode)}
        onChange={(value) => {
          setCountryCode(value as CountryAutoComplete);
          setProvinceCode(undefined);
          setCityCode(undefined);
        }}
        testId="billing-address-country"
      >
        {(selectProps: SelectRenderProps) => (
          <AutoCompleteCountriesSearchContainer {...selectProps} placeHolder={t('billing.map.countryModal')} />
        )}
      </CountriesAutoCompleteSelect>
    </ThemeProvider>
  );

  const renderProvinceSelect = () => (
    <ThemeProvider theme={customTheme}>
      <ProvincesAutoCompleteSelect
        label={t('billing.map.province')}
        detail={t('billing.map.provincePlaceholder')}
        value={getProvinceSelectValue(provinceCode)}
        onChange={(value) => {
          setProvinceCode(value as ProvinceAutoComplete);
          setCityCode(undefined);
        }}
        testId="billing-address-province"
      >
        {(selectProps: ProvinceSelectRenderProps) => (
          <AutoCompleteProvincesSearchContainer
            {...selectProps}
            countryCode={countryCode?.countryCode}
            placeHolder={t('billing.map.provinceModal')}
          />
        )}
      </ProvincesAutoCompleteSelect>
    </ThemeProvider>
  );

  const renderCitySelect = () => (
    <ThemeProvider theme={customTheme}>
      <CitiesAutoCompleteSelect
        label={t('billing.map.city')}
        detail={t('billing.map.cityPlaceholder')}
        value={getCitySelectValue(cityCode)}
        onChange={(value) => setCityCode(value as CityAutoComplete)}
        testId="billing-address-city"
      >
        {(selectProps: SelectRenderProps) => (
          <AutoCompleteCitiesSearchContainer
            country={countryCode?.countryCode}
            {...selectProps}
            placeHolder={t('billing.map.cityModal')}
          />
        )}
      </CitiesAutoCompleteSelect>
    </ThemeProvider>
  );

  return (
    <Wrapper className="target">
      <Container>
        <Desktop>
          <ScreenHeader title={t('billing.profile.title')} />
        </Desktop>

        <Mobile>
          <Header goBack={handleBack}>{t('billing.profile.title')}</Header>
        </Mobile>

        <Content>
          <Title>{t('billing.map.title')}</Title>

          <Formik
            initialStatus={{ rightCheckIfIsValid: true }}
            initialValues={initValues}
            validationSchema={validationSchema}
            onSubmit={(value) => handleSubmit(value as ICompanyBillingMapForm)}
            isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
          >
            {({ dirty }: FormikValues) => (
              <FormContainer $isDirty={dirty}>
                <LocationFields>
                  <Field name="address" label={t('billing.map.address')}>
                    <Input id="address" placeholder={t('billing.map.addressPlaceholder')} />
                  </Field>
                  <Field name="description" label={t('billing.map.description')}>
                    <Input id="description" placeholder={t('billing.map.descriptionPlaceholder')} />
                  </Field>

                  {renderCountrySelect()}
                  {renderProvinceSelect()}
                  {renderCitySelect()}

                  <Field name="zipCode" >
                    <Input id="zipCode" placeholder={t('billing.map.postalCodePlaceholder')} />
                  </Field>
                </LocationFields>

                <ButtonContainer>
                  <SubmitButton isEdit={false} dirty={dirty} loading={loading || isSubmitting}>
                    {t('billing.map.buttonConfirm')}
                  </SubmitButton>
                </ButtonContainer>
              </FormContainer>
            )}
          </Formik>
        </Content>
      </Container>
    </Wrapper>
  );
};

export default CompanyBillingMapForm;
